import { zodResolver } from '@hookform/resolvers/zod';
import { useRef } from 'react';
import {
  type FieldValues,
  FormProvider,
  type SubmitHandler,
  useForm,
  type UseFormProps,
} from 'react-hook-form';
import type { z } from 'zod';

type FormProps<
  TFieldValues extends FieldValues,
  TSchema extends z.ZodType<FieldValues> = z.ZodType<FieldValues>,
> = Omit<UseFormProps<TFieldValues>, 'resolver' | 'defaultValues'> & {
  schema?: TSchema;
  children: React.ReactNode;
  onSubmit: SubmitHandler<TFieldValues>;
  defaultValues?: z.input<TSchema>;
  /**
   * Skip the browser's own checks (e.g. `type="email"`) so the schema's
   * inline messages show instead of the native bubble. Needed with
   * `mode="onSubmit"`, where the browser would block the submit first.
   */
  noValidate?: boolean;
};

const focusableControlSelector = [
  'input:not([type="hidden"]):not([disabled]):not([aria-disabled="true"])',
  'textarea:not([disabled]):not([aria-disabled="true"])',
  'button:not([disabled]):not([aria-disabled="true"])',
  'select:not([disabled]):not([aria-disabled="true"])',
  '[tabindex]:not([tabindex^="-"]):not([aria-disabled="true"])',
].join(', ');

export const Form = <
  TFieldValues extends FieldValues,
  TSchema extends z.ZodType<FieldValues> = z.ZodType<FieldValues>,
>({
  schema,
  children,
  onSubmit,
  defaultValues,
  noValidate,
  ...props
}: FormProps<TFieldValues, TSchema>) => {
  const formRef = useRef<HTMLFormElement>(null);
  const formProps = useForm<TFieldValues>({
    mode: 'onBlur',
    shouldFocusError: false,
    ...props,
    // Zod 4 keeps `z.input<TSchema>` deferred while TSchema is generic, so
    // name the form values the schema parses to and from.
    defaultValues: defaultValues as UseFormProps<TFieldValues>['defaultValues'],
    resolver: schema
      ? zodResolver(schema as z.ZodType<TFieldValues, TFieldValues>)
      : undefined,
  });

  const handleInvalidSubmit = () => {
    requestAnimationFrame(() => {
      const invalidControl = formRef.current?.querySelector<HTMLElement>(
        '[data-slot="form-control"][aria-invalid="true"]',
      );

      if (!invalidControl) return;

      invalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const focusTarget = invalidControl.matches(focusableControlSelector)
        ? invalidControl
        : invalidControl.querySelector<HTMLElement>(focusableControlSelector);

      focusTarget?.focus({ preventScroll: true });
    });
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    void formProps.handleSubmit(onSubmit, handleInvalidSubmit)(event);
  };

  return (
    <FormProvider {...formProps}>
      <form ref={formRef} noValidate={noValidate} onSubmit={handleFormSubmit}>
        {children}
      </form>
    </FormProvider>
  );
};
